/**
 * The legal texts in English (docs/LEGAL.md), shown to every reader who is not reading Turkish.
 * The Turkish ones in `tr.ts` say the same, section for section: change both together, and bump
 * `LEGAL_VERSION` (documents.ts).
 */

import type { LegalTexts } from '../documents';

export const EN_TEXTS: LegalTexts = {
  updated: '29 October 2026',
  documents: {
    terms: {
      title: 'Terms of Use',
      summary:
        'The rules for using QuantPhi: what the service is and is not, your account, your exchange keys, packages and liability.',
      sections: [
        {
          id: 'scope',
          heading: '1. Parties and scope',
          blocks: [
            'These Terms of Use (the “Terms”) govern the use of the website and app at quantphi.app, the Telegram bot and the other interfaces connected to them (together “QuantPhi” or the “Service”). The Service is provided by QuantPhi at quantphi.app (“we”, “us”); our contact details are in the {privacy}.',
            'By creating an account you confirm that you have read and accept these Terms and the {risk}; how your personal data is processed is explained in the {privacy}. If you do not accept them, do not use the Service.',
          ],
        },
        {
          id: 'tool',
          heading: '2. The Service is a software tool',
          blocks: [
            'QuantPhi is software that places crypto-asset orders automatically, on your own exchange account, by rules you set. It includes DCA, grid, rebalance, recurring-buy and Hedge Stop bots, an autopilot, a market scanner, backtests and a showcase.',
            [
              'QuantPhi is not an exchange, a broker, a crypto-asset service provider, a portfolio manager or an investment adviser.',
              'It does not hold, accept, transfer or withdraw your money or crypto assets. Your assets always stay on your own exchange account.',
              'Orders are placed on the exchange account you connect, with your API key, and with settings you made or approved.',
              'Whether to start, change or stop a bot, the autopilot or a suggested setting is your decision alone.',
            ],
          ],
        },
        {
          id: 'advice',
          heading: '3. Not investment advice',
          blocks: [
            'Nothing on the website, in the app, on Telegram or on our social media accounts — including the market scanner’s suggestions, the autopilot’s picks, showcase and results cards, backtest results and default settings — is investment advice, a recommendation to buy or sell, or investment advisory service within the meaning of the Turkish Capital Markets Law No. 6362 and related regulations.',
            'This content is the result of mechanical calculations the software makes on past price data and the chosen settings. It is produced without regard to your financial situation or your risk and return preferences, so it may not suit you. Investment advisory services are provided only by authorised institutions under an investment advisory agreement.',
            'Past results and backtests do not guarantee future returns. No package, bot or setting guarantees a profit.',
          ],
        },
        {
          id: 'account',
          heading: '4. Account and eligibility',
          blocks: [
            [
              'You must be at least 18 and have full legal capacity. If the law where you are prohibits crypto-asset trading or the use of such software, do not use the Service.',
              'You are responsible for keeping your registration details accurate and current, for keeping your password secret, and for what is done in your account.',
              'If you notice unauthorised use of your account, tell us at once at {email}.',
              'You may not transfer, sell or rent your account to anyone else.',
            ],
          ],
        },
        {
          id: 'keys',
          heading: '5. Your exchange account and API keys',
          blocks: [
            [
              'Your exchange account and the assets in it are yours. Your relationship with the exchange is governed by that exchange’s own terms, and complying with them is your responsibility.',
              'Create your API keys with read and trade permissions only; never enable withdrawals. QuantPhi never withdraws and has no feature that needs withdrawal permission.',
              'Your keys are stored encrypted on our servers. You can revoke a key on your exchange or delete it in the app at any time. Deleting a key does not close the orders and positions still open on the exchange.',
              'Events caused by the exchange — outages, rejected orders, slippage, lack of liquidity, funding fees, liquidation, account restrictions, delistings or the exchange’s insolvency — are outside QuantPhi’s control.',
            ],
          ],
        },
        {
          id: 'plans',
          heading: '6. Packages and payments',
          blocks: [
            [
              'Some features of the Service require a paid package. The contents, duration and price of each package are shown on the website and in the app. Payments are made in USDT, on the network and for the amount shown in the app.',
              'Trials, campaigns and discount codes are limited to the conditions and periods announced. We may change or end them going forward; a package period that has already started is not affected.',
              'Checking that a transfer goes to the right network and address, for the full amount, is your responsibility. Blockchain transfers cannot be reversed; we may be unable to recover an amount sent to the wrong network or address.',
              'A package becomes active electronically as soon as its payment is confirmed. Therefore, without prejudice to your rights under the consumer law that applies, fees for a package period that has started are not refunded.',
            ],
          ],
        },
        {
          id: 'rules',
          heading: '7. Rules of use',
          blocks: [
            'You may not:',
            [
              'use the Service for unlawful purposes, market abuse or price manipulation;',
              'connect an exchange account that is not yours or that you are not authorised to use;',
              'try to break the system’s security, reverse-engineer the software, overload the Service or collect data from it by automated means without permission;',
              'claim trial, campaign or discount benefits with more than one account.',
            ],
            'If you break these rules we may suspend or close your account.',
          ],
        },
        {
          id: 'showcase',
          heading: '8. The showcase and the results page',
          blocks: [
            'The showcase and the public results page show the results of bots running on the platform anonymously (under a random name, with no amounts or dates, in percent) and the system’s own backtests. Only bots with a positive return are shown, so the cards do not reflect the average result of the bots on the platform.',
            'Copying a card does not copy its result: the same settings, on your account, with your amounts and at another time, may do differently and may lose money. Backtest cards are not real trades.',
          ],
        },
        {
          id: 'availability',
          heading: '9. Availability of the Service',
          blocks: [
            'The Service is provided “as is” and “as available”; we do not promise that it will run without interruption or error. Because of server, network, exchange API or software failures, bots may fail to place orders, place them late or fail to cancel them.',
            'We may carry out maintenance and updates, and change or remove features. When the Service stops, orders and positions open on the exchange stay there; watching them on your exchange is your responsibility.',
          ],
        },
        {
          id: 'liability',
          heading: '10. Limitation of liability',
          blocks: [
            'Your investment decisions and their outcomes, your exchange account and API keys, your bot and autopilot settings and the data you enter into the Service are entirely your responsibility. The profit and the loss of every order you place with QuantPhi are yours.',
            'To the extent the law allows, QuantPhi is not liable for direct or indirect losses or lost profits arising from market movements, settings you made or approved, leverage and liquidation, events caused by the exchange or other third-party services, or the software not working as expected.',
            'In any case our total liability is limited to the package fees you paid us in the 12 months before the loss arose. Liability for intent and gross negligence, and your mandatory rights under consumer law, are not affected.',
          ],
        },
        {
          id: 'ip',
          heading: '11. Intellectual property',
          blocks: [
            'The software, the design, the content and the QuantPhi and HopeBot names and logos belong to us. You get a non-transferable, non-exclusive right to use the Service for your own account in line with these Terms.',
          ],
        },
        {
          id: 'closing',
          heading: '12. Closing your account',
          blocks: [
            'You can ask us to close your account at any time at {email}. When an account is closed its bots are stopped; orders and positions still open on the exchange stay on your exchange. How long your data is kept is set out in the {privacy}.',
          ],
        },
        {
          id: 'changes',
          heading: '13. Changes',
          blocks: [
            'We may update these Terms; the current version and its date are on this page. We announce important changes on the website or in the app and ask for your acceptance again where needed. Continuing to use the Service after a change means you accept the updated Terms.',
          ],
        },
        {
          id: 'law',
          heading: '14. Governing law and disputes',
          blocks: [
            'These Terms are governed by Turkish law. The courts and enforcement offices of Türkiye have jurisdiction over disputes. Users acting as consumers keep their right to apply to consumer arbitration committees and consumer courts.',
          ],
        },
        {
          id: 'contact',
          heading: '15. Contact',
          blocks: ['Questions: {email}'],
        },
      ],
    },

    privacy: {
      title: 'Privacy and KVKK Notice',
      summary:
        'Which personal data we process, for what purpose and on what legal ground, to whom we transfer it, and your rights. Prepared under Article 10 of the Turkish Personal Data Protection Law No. 6698 (KVKK).',
      sections: [
        {
          id: 'controller',
          heading: '1. Data controller',
          blocks: [
            'Your personal data is processed by the following data controller:',
            ['Name: QuantPhi', 'Website: quantphi.app', 'E-mail: {email}'],
          ],
        },
        {
          id: 'data',
          heading: '2. Personal data we process',
          blocks: [
            [
              'Identity and contact: your e-mail address and, if you give it, your name.',
              'Account security: an irreversible hash of your password (the password itself is not stored), session records, IP address and browser details (user agent).',
              'Exchange connection: the exchange, the name you gave the connection, the API key and secret (stored encrypted).',
              'Trading data: your bots and their settings; orders, deals, positions, balances and profit and loss — including what is read from your exchange with your API key.',
              'Payment data: the package you chose, the amount, network, transaction ID (TxID), payment tag, coupon and campaign. We do not process card details.',
              'Sign-up source: the campaign tags (utm), referral and coupon code of the link you arrived through.',
              'Telegram (if you link it): your Telegram user ID, username and chat ID.',
              'Preferences and correspondence: your language, theme and notification settings; e-mails you send us.',
            ],
          ],
        },
        {
          id: 'purposes',
          heading: '3. Why we process it',
          blocks: [
            [
              'to open and manage your account;',
              'to have bots place orders on your exchange account by the rules you set, and to show your balances, positions and results;',
              'to run backtests, the market scanner, the autopilot and the showcase;',
              'to run packages, payments, trials and campaigns, and send reminders before a package ends;',
              'to send in-app and Telegram notifications and answer your support requests;',
              'to keep the Service secure: preventing unauthorised access and abuse, limiting sign-in attempts;',
              'to measure which links bring sign-ups;',
              'to meet our legal obligations and protect our rights in a dispute.',
            ],
          ],
        },
        {
          id: 'grounds',
          heading: '4. Legal grounds',
          blocks: [
            'We process your personal data on these grounds of KVKK Article 5(2):',
            [
              '(c) it is directly necessary to conclude or perform a contract — providing the service under the {terms};',
              '(ç) it is necessary to meet our legal obligations — for example keeping payment records;',
              '(e) it is necessary to establish, exercise or protect a right;',
              '(f) it is necessary for our legitimate interests, provided your fundamental rights and freedoms are not harmed — security logs, measuring sign-up sources and anonymous statistics.',
            ],
            'For any processing outside these grounds we ask for your explicit consent separately.',
          ],
        },
        {
          id: 'collection',
          heading: '5. How we collect it',
          blocks: [
            'We collect your data through the sign-up and in-app forms, your exchange’s API, the Telegram bot and e-mail, and automatically through server logs. Your USDT payments are matched with the transfers arriving at QuantPhi’s Binance account.',
            'Your browser’s local storage (localStorage) holds only what the Service needs to work: the session key, your language and theme, a coupon code that came with a link, and the sign-up source. We use no advertising or analytics cookies and no third-party trackers.',
          ],
        },
        {
          id: 'transfer',
          heading: '6. Transfers, including abroad',
          blocks: [
            'We do not sell your personal data or share it for advertising. We transfer it only as far as needed to provide the Service, to these recipients:',
            [
              'Hosting: Oracle Cloud Infrastructure. Our servers and database are in Oracle’s Frankfurt (Germany) data centre.',
              'The exchange you connect (for example Bybit, OKX, Bitget, Binance): orders and read requests are sent to your exchange on your instruction and with your API key. The exchange’s own privacy policy applies.',
              'Telegram: if you link it, to deliver your notifications.',
              'Cloudflare (domain and e-mail routing) and Google (Gmail): so that e-mails you send to {email} reach us.',
              'Authorised public authorities and courts: where the law requires it.',
            ],
            'Because our servers are in Germany and some of the providers above are outside Turkey, your personal data is transferred abroad. These transfers are made in line with the conditions of KVKK Article 9. Requests to your exchange are sent on your instruction, to perform the service.',
          ],
        },
        {
          id: 'showcase',
          heading: '7. The showcase and anonymous statistics',
          blocks: [
            'The results of bots running on the platform may be shown anonymously in the showcase and on the public results page: a randomly generated name, the coin, the exchange, the strategy settings (as ratios instead of amounts), the number of days and returns in percent. Your name, e-mail, account, amounts, the bot’s ID and dates are never shown; the cards are built so they cannot be linked to you. For questions or requests about this, write to {email}.',
          ],
        },
        {
          id: 'retention',
          heading: '8. How long we keep it',
          blocks: [
            [
              'Account data is kept while your account is open.',
              'When you delete an exchange connection, its encrypted API keys are deleted with it.',
              'When your account is closed, your data is kept for the periods the law sets (for example the periods in tax and commercial law for payment and accounting records) and for the limitation periods of possible disputes, and then deleted, destroyed or anonymised.',
            ],
          ],
        },
        {
          id: 'rights',
          heading: '9. Your rights under KVKK Article 11',
          blocks: [
            'By applying to the data controller you may:',
            [
              'learn whether your personal data is processed;',
              'ask for information about it if it is;',
              'learn the purpose of the processing and whether the data is used for that purpose;',
              'know the third parties in Turkey or abroad it is transferred to;',
              'ask for it to be corrected if it is incomplete or wrong;',
              'ask for it to be deleted or destroyed under the conditions of KVKK Article 7;',
              'ask for corrections, deletions and destructions to be notified to the third parties it was transferred to;',
              'object to a result against you that arises from analysis solely by automated systems;',
              'claim compensation if you suffer damage from unlawful processing.',
            ],
          ],
        },
        {
          id: 'apply',
          heading: '10. How to apply',
          blocks: [
            'Send your application by e-mail to {email} from the e-mail address registered in our system, or in writing to the data controller’s address above. State your name, your account’s e-mail address and your request clearly.',
            'We answer within 30 days at the latest, free of charge. If the request involves a separate cost, the fee in the tariff set by the Personal Data Protection Board may be charged.',
          ],
        },
        {
          id: 'security',
          heading: '11. Security',
          blocks: [
            'We store your API keys encrypted and your password as a one-way hash. The connection between you and the site is encrypted with HTTPS (TLS), and access to the servers and the database is limited to authorised people. No system is completely secure: creating your API keys without withdrawal permission limits the impact of any leak.',
          ],
        },
        {
          id: 'children',
          heading: '12. Children',
          blocks: [
            'The Service is not meant for anyone under 18, and we do not knowingly process their data.',
          ],
        },
        {
          id: 'changes',
          heading: '13. Changes',
          blocks: [
            'We may update this notice; the current version and its date are on this page. We announce important changes on the website or in the app.',
          ],
        },
      ],
    },

    risk: {
      title: 'Risk Notice',
      summary:
        'Crypto-asset trading and automated bots carry serious risks. Read this before you use QuantPhi.',
      sections: [
        {
          id: 'loss',
          heading: '1. You can lose all of your money',
          blocks: [
            'Crypto-asset prices are highly volatile; they can lose most or all of their value in a short time. Trade only with amounts you can afford to lose.',
          ],
        },
        {
          id: 'leverage',
          heading: '2. Leverage and futures',
          blocks: [
            'With leveraged futures, a small adverse price move can wipe out your whole margin, and the exchange can liquidate your position. On cross margin, the loss of one position also eats into your other positions and your balance. Funding fees change the result for as long as a position stays open.',
          ],
        },
        {
          id: 'strategies',
          heading: '3. The strategies’ own risks',
          blocks: [
            [
              'DCA bots add to the position with safety orders as the price falls: if the fall continues, the loss and the capital tied up grow too, and a deal can stay at a loss for a long time.',
              'Grid bots end up outside their range in a sharp, one-way move; you may be left holding an asset that keeps losing value.',
              'Protections such as stop loss and time limits may not work in every condition, or may not fill at the price you expect.',
              'Hedge and recovery features do not always limit a loss; an extra position means extra margin and extra risk.',
            ],
          ],
        },
        {
          id: 'past',
          heading: '4. Past results and backtests are no guarantee',
          blocks: [
            [
              'Past results and backtests do not guarantee future returns.',
              'Backtests are simulations on past price data: slippage, order-book depth, latency and exchange outages are not modelled; fees and funding are approximate.',
              'When many settings are tried, some look good by luck alone. The showcase and the results page show only bots with a positive return; that is not the average result of the bots on the platform.',
              'Percentage returns are measured against the most capital committed at once; on your account, with your amounts, the result may differ.',
            ],
          ],
        },
        {
          id: 'automation',
          heading: '5. The autopilot and the market scanner',
          blocks: [
            'The market scanner and the autopilot make automatic, rule-based picks. These picks are not investment advice and can fail when market conditions change. Once you switch the autopilot on, the bots it opens trade with real money on your account.',
          ],
        },
        {
          id: 'technical',
          heading: '6. Technical risks',
          blocks: [
            'Because of server, internet, exchange API or software failures, orders may be delayed, not sent, or not cancelled. When the Service stops, orders and positions open on the exchange stay there.',
          ],
        },
        {
          id: 'exchange',
          heading: '7. Exchange risks',
          blocks: [
            'Your assets sit on the exchange you connect. The exchange going insolvent, being hacked, freezing your account, delisting a coin or changing its rules is outside QuantPhi’s control.',
          ],
        },
        {
          id: 'regulation',
          heading: '8. Law and tax',
          blocks: [
            'Rules on crypto assets differ from country to country and can change. Checking that this trading is allowed where you are, and meeting your tax obligations, is your responsibility.',
          ],
        },
        {
          id: 'advice',
          heading: '9. Not investment advice',
          blocks: [
            'QuantPhi is a software tool; it does not give investment advice. Make your decisions on your own judgement and, where needed, get help from an authorised investment adviser. Your investment decisions and their outcomes are entirely your responsibility. The details are in the {terms}.',
          ],
        },
      ],
    },
  },
};
